import type { CompanyProcedureDto, CompanyProcedureRoleDto, CompanyProceduresState } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, PageTitle, SectionLoader, SectionToggle } from '../../ui';
import { useLevels } from './Livelli';
import { ProcedureWork } from './ProcedureWork';
import { SectionQuestions } from './SectionQuestions';

const STATUS: Record<CompanyProcedureDto['stato'], { label: string; tone: 'green' | 'amber' | 'gray' }> = {
  vale: { label: 'vale', tone: 'green' },
  da_confermare: { label: 'da confermare', tone: 'amber' },
  esclusa: { label: 'esclusa', tone: 'gray' },
};

/** Chi ricopre il ruolo: le persone e le cariche; altrimenti chi lo copre di solito, un esterno o «da assegnare». */
function Who({ label, r }: { label: string; r: CompanyProcedureRoleDto }) {
  return (
    <>
      {label}: <span className="text-ink/80">{r.nome}</span>
      {r.persone.length ? (
        <> ({r.persone.join(', ')})</>
      ) : r.di_solito === 'esterno' ? (
        <> (da affidare a un esterno{r.esterno_tipico ? `: ${r.esterno_tipico}` : ''})</>
      ) : (
        <>
          {' '}
          (<span className="text-amber-700">da assegnare</span>
          {r.coperto_da ? `; di solito lo copre: ${r.coperto_da}` : ''})
        </>
      )}
    </>
  );
}

/**
 * Le procedure dell'azienda (ADR 0214): composte dalla libreria nella Fase 0 dopo i ruoli, per aree, con la cadenza, lo
 * stato e il motivo, chi la svolge e chi ne risponde, la prova e i passi. Seguono il livello scelto in alto (ADR 0212):
 * nell'azienda quelle dell'azienda e per dato, in una sede quelle della sede. Le domande della sezione decidono quelle da
 * confermare; il titolare le attiva, una per una o tutte quelle che valgono in un'area.
 */
export function ProceduresPage() {
  const { t, tenant, isManager, livello } = useTenant();
  const levels = useLevels();
  const qc = useQueryClient();
  // Il livello dell'elenco: la sede scelta; con più sedi e nessuna scelta, l'azienda; senza livelli tutto.
  const level = levels.site ? livello : levels.multi ? 'azienda' : null;
  const query = level ? `?livello=${level}` : '';
  const q = useTenantQuery<CompanyProceduresState>(['procedures', level ?? 'tutte'], `/company-procedures${query}`);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [showExcluded, setShowExcluded] = useState(false);
  // Le aree si aprono una alla volta, quando servono.
  const [openAreas, setOpenAreas] = useState<Set<string>>(new Set());
  const refresh = () => qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'procedures'] });
  const activate = useMutation({
    mutationFn: ({ id, attiva }: { id: string; attiva: boolean }) => api(t(`/company-procedures/${id}${query}`), { method: 'PATCH', body: { attiva } }),
    onSuccess: refresh,
  });
  const activateArea = useMutation({ mutationFn: (area: string) => api(t('/company-procedures/area'), { body: { area, livello: level } }), onSuccess: refresh });
  const compose = useMutation({ mutationFn: () => api(t(`/company-procedures/compose${query}`), { body: {} }), onSuccess: refresh });

  if (q.isLoading) return <SectionLoader />;
  if (!q.data) return <ErrorText>{q.error ? errorMessage(q.error) : 'Procedure non disponibili'}</ErrorText>;
  const { aree, procedure } = q.data;
  // Aree e procedure in ordine alfabetico; «Altre procedure» in fondo.
  const byName = (a: { nome: string }, b: { nome: string }) => a.nome.localeCompare(b.nome, 'it');
  const live = procedure.filter((p) => p.stato !== 'esclusa').sort(byName);
  const excluded = procedure.filter((p) => p.stato === 'esclusa').sort(byName);
  const groups = [...[...aree].sort(byName).map((a) => ({ ...a, items: live.filter((p) => p.area === a.chiave) })), { chiave: 'altre', nome: 'Altre procedure', items: live.filter((p) => !aree.some((a) => a.chiave === p.area)) }].filter((g) => g.items.length);
  const toggle = (id: string) => setOpen((s) => new Set(s.has(id) ? [...s].filter((x) => x !== id) : [...s, id]));
  const busy = activate.isPending || activateArea.isPending;

  const item = (p: CompanyProcedureDto) => (
    <li key={p.id} className="py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{p.nome}</span>
        <Badge tone={STATUS[p.stato].tone}>{STATUS[p.stato].label}</Badge>
        {p.attiva && <Badge tone="blue">attiva</Badge>}
        {p.base === 'legge' && <Badge>di legge</Badge>}
        {isManager && (
          <span className="ml-auto">
            {p.attiva ? (
              <button type="button" className="text-xs text-ink/50 hover:text-ink" disabled={busy} onClick={() => activate.mutate({ id: p.id, attiva: false })}>
                Disattiva
              </button>
            ) : (
              <Button variant="ghost" className="px-2 py-1 text-xs" disabled={busy} onClick={() => activate.mutate({ id: p.id, attiva: true })}>
                Attiva
              </Button>
            )}
          </span>
        )}
      </div>
      {p.descrizione && <p className="mt-0.5 text-xs text-ink/70">{p.descrizione}</p>}
      <p className="mt-1 text-xs text-ink/60">
        {p.cadenza}
        {p.nota_cadenza && <> ({p.nota_cadenza})</>}
        {p.dove === 'per' && p.per && <> · per ogni {p.per.replace(/_/g, ' ')}</>}
        {p.sede && <> · {p.sede}</>}
        {p.motivo && <> · {p.motivo}</>}
      </p>
      <p className="mt-1 text-xs text-ink/60">
        <Who label="La svolge" r={p.la_svolge} />
        {p.ne_risponde.chiave !== p.la_svolge.chiave && (
          <>
            {' · '}
            <Who label="Ne risponde" r={p.ne_risponde} />
          </>
        )}
      </p>
      <p className="mt-1 text-xs text-ink/60">
        Prova: {p.prova}
        {p.norma && <> · {p.norma}</>}
      </p>
      <ProcedureWork p={p} />
      {p.passi.length > 0 && (
        <button type="button" className="mt-1 text-xs text-accent hover:underline" onClick={() => toggle(p.id)}>
          {open.has(p.id) ? 'Nascondi i passi' : `I passi (${p.passi.length})`}
        </button>
      )}
      {open.has(p.id) && (
        <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-xs text-ink/70">
          {p.passi.map((s, i) => (
            <li key={i}>
              {s.nome}
              {s.chi && <span className="text-ink/50"> · {s.chi}</span>}
            </li>
          ))}
        </ol>
      )}
    </li>
  );

  return (
    <>
      <PageTitle
        title={levels.site ? `Procedure · ${levels.site.name}` : 'Procedure'}
        subtitle={
          levels.site
            ? 'Il lavoro che si ripete in questa sede, dalla libreria del suo mestiere. Le procedure di tutta l’azienda si vedono in «Tutta l’azienda».'
            : 'Il lavoro che si ripete, dalla libreria del mestiere dell’azienda: quando si fa, chi la svolge e la prova che lascia. Una procedura attivata diventerà compiti.'
        }
        actions={isManager ? <Button variant="ghost" onClick={() => compose.mutate()} disabled={compose.isPending}>Ricomponi dalla libreria</Button> : undefined}
      />
      <SectionQuestions sezione="procedure" />
      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <Badge>{live.length} procedure</Badge>
        {q.data.da_confermare > 0 && <Badge tone="amber">{q.data.da_confermare} da confermare</Badge>}
        <Badge tone="blue">{q.data.attive} attive</Badge>
      </div>
      {compose.error && <ErrorText>{errorMessage(compose.error)}</ErrorText>}
      {activate.error && <ErrorText>{errorMessage(activate.error)}</ErrorText>}
      {activateArea.error && <ErrorText>{errorMessage(activateArea.error)}</ErrorText>}
      {!live.length && <Empty>Le procedure si compongono con i ruoli, dopo la lettura della visura.</Empty>}
      <div className="space-y-4">
        {groups.map((g) => {
          const ready = g.items.filter((p) => p.stato === 'vale' && !p.attiva).length;
          return (
            <Card key={g.chiave} className={openAreas.has(g.chiave) ? '' : '!py-4'}>
              <div className="flex flex-wrap items-center gap-2">
                <SectionToggle title={g.nome} count={g.items.length} open={openAreas.has(g.chiave)} onToggle={() => setOpenAreas((s) => new Set(s.has(g.chiave) ? [...s].filter((x) => x !== g.chiave) : [...s, g.chiave]))} />
                {isManager && ready > 0 && (
                  <Button variant="ghost" className="ml-auto px-2 py-1 text-xs" disabled={busy} onClick={() => activateArea.mutate(g.chiave)}>
                    Attiva le {ready} che valgono
                  </Button>
                )}
              </div>
              {openAreas.has(g.chiave) && <ul className="mt-1 divide-y divide-ink/10">{g.items.map(item)}</ul>}
            </Card>
          );
        })}
      </div>
      {excluded.length > 0 && (
        <div className="mt-4 px-1">
          <button type="button" className="text-xs text-ink/55 underline decoration-ink/25 underline-offset-2 hover:text-ink" onClick={() => setShowExcluded((v) => !v)}>
            {showExcluded ? 'Nascondi' : 'Mostra'} {excluded.length === 1 ? 'la procedura che non serve' : `le ${excluded.length} procedure che non servono`}
          </button>
          {showExcluded && (
            <ul className="mt-2 divide-y divide-ink/10">
              {excluded.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm text-ink/60">
                  <span>{p.nome}</span>
                  {p.sede && <span className="text-xs text-ink/50">{p.sede}</span>}
                  {p.motivo && <span className="text-xs text-ink/50">{p.motivo}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
